/**
 * /admin/exams — every exam, newest scheduled first.
 *
 * The list doubles as the invigilator's way in during a sitting, so it carries
 * how many attempts each exam has and how many of those are running right now.
 * Neither number is on `ExamSummary` — the contract only says whether *an*
 * attempt exists — so they are counted client-side from one attempts call and
 * grouped by exam. Cheap at this app's scale, and easy to delete the day the
 * API serves the counters itself.
 *
 * The "For" column is the other thing the list has to say now that an exam is
 * no longer automatically everyone's: `assigned_count` of zero means EVERY
 * student, not none, and an API build that predates assignment omits the field
 * entirely. Those two cases look identical if you render the number naively,
 * which is why they are separated below.
 */
import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Lock } from "lucide-react";
import {
  Badge,
  Button,
  Card,
  CopyButton,
  CardHeader,
  Select,
  Table,
  TableEmpty,
  TBody,
  TD,
  TH,
  THead,
  TR,
} from "@/components/ui";
import type { BadgeTone } from "@/components/ui";
import type { ExamStatus } from "@exam/shared";
import type { ExamRow } from "./data/types";
import { useCatalog } from ".";
import { ErrorNote, FixtureBanner, Loading, PageTitle } from "./components/Shell";
import { LiveMonitorLink } from "./components/ExamActions";
import { fetchAttempts, fetchExams } from "./data/client";
import { formatDateTime, formatMarks, formatRelativeDays } from "./lib/format";
import { useAsync } from "./lib/useAsync";

const STATUS_TONE: Record<ExamStatus, BadgeTone> = {
  draft: "neutral",
  scheduled: "accent",
  live: "review",
  done: "answered",
};

export function ExamsPage() {
  const catalog = useCatalog();
  const [status, setStatus] = useState<string>("");
  const [subjectId, setSubjectId] = useState<string>("");

  const state = useAsync(
    () =>
      fetchExams(catalog, {
        ...(status ? { status } : {}),
        ...(subjectId ? { subject_id: subjectId } : {}),
      }),
    [catalog, status, subjectId],
  );

  // Deliberately unfiltered and fetched once: the attempt counts belong to the
  // exams, not to the filter, so re-reading them every time the subject
  // dropdown moves would be pure waste.
  const attempts = useAsync(() => fetchAttempts(), []);

  const countsByExam = useMemo(() => {
    const acc = new Map<string, { total: number; running: number }>();
    for (const row of attempts.data?.attempts ?? []) {
      const entry = acc.get(row.attempt.exam_id) ?? { total: 0, running: 0 };
      entry.total += 1;
      if (row.attempt.status === "in_progress") entry.running += 1;
      acc.set(row.attempt.exam_id, entry);
    }
    return acc;
  }, [attempts.data]);

  return (
    <div className="space-y-4">
      <PageTitle
        title="Exams"
        meta="Build a paper, preview it, then schedule it."
        actions={
          <Link to="/admin/exams/new">
            <Button variant="primary">New exam</Button>
          </Link>
        }
      />

      <FixtureBanner />

      <Card>
        <CardHeader
          title="All exams"
          actions={
            <div className="flex items-end gap-2">
              <Select
                label="Subject"
                value={subjectId}
                onChange={(e) => setSubjectId(e.target.value)}
                className="w-56"
              >
                <option value="">All subjects</option>
                {catalog.subjects.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.name}
                  </option>
                ))}
              </Select>
              <Select
                label="Status"
                value={status}
                onChange={(e) => setStatus(e.target.value)}
                className="w-32"
              >
                <option value="">All</option>
                <option value="draft">Draft</option>
                <option value="scheduled">Scheduled</option>
                <option value="live">Live</option>
                <option value="done">Done</option>
              </Select>
            </div>
          }
        />

        {state.loading ? (
          <Loading />
        ) : state.error ? (
          <div className="p-4">
            <ErrorNote error={state.error} />
          </div>
        ) : (
          <Table>
            <THead>
              <tr>
                <TH>Exam</TH>
                <TH>Subject</TH>
                <TH>Weeks</TH>
                <TH>Scheduled</TH>
                <TH align="right">Duration</TH>
                <TH align="right">Questions</TH>
                <TH align="right">Marks</TH>
                <TH>Attempts</TH>
                <TH>For</TH>
                <TH>Status</TH>
                <TH align="right">{""}</TH>
              </tr>
            </THead>
            <TBody>
              {(state.data ?? []).length === 0 ? (
                <TableEmpty colSpan={11}>No exams match these filters.</TableEmpty>
              ) : (
                state.data!.map((exam) => (
                  <TR key={exam.id}>
                    <TD>
                      <Link
                        to={`/admin/exams/${exam.id}`}
                        className="font-medium text-ink hover:underline"
                      >
                        {exam.title}
                      </Link>
                      {exam.frozen ? (
                        <span
                          className="ml-1.5 inline-flex items-center align-middle text-ink-faint"
                          title="The paper is frozen — a student has started this exam."
                        >
                          <Lock className="size-3" />
                        </span>
                      ) : null}
                    </TD>
                    <TD className="text-ink-muted">{exam.subject_name}</TD>
                    <TD className="tabular text-ink-muted">
                      {exam.covers_weeks.length === 0
                        ? "—"
                        : summariseWeeks(exam.covers_weeks)}
                    </TD>
                    <TD>
                      {exam.timing_mode === "from_start" ? (
                        <span className="text-ink-muted">
                          Anytime
                          <span className="ml-1.5 text-ink-faint">
                            timer starts on begin
                          </span>
                        </span>
                      ) : (
                        <>
                          <time>{formatDateTime(exam.scheduled_at)}</time>
                          <span className="ml-1.5 text-ink-faint">
                            {formatRelativeDays(exam.scheduled_at)}
                          </span>
                        </>
                      )}
                    </TD>
                    <TD align="right" className="tabular">
                      {exam.duration_min}m
                    </TD>
                    <TD align="right" className="tabular">
                      {exam.question_count}
                    </TD>
                    <TD align="right" className="tabular">
                      {formatMarks(exam.total_marks)}
                    </TD>
                    <TD>
                      <AttemptsCell
                        examId={exam.id}
                        counts={countsByExam.get(exam.id) ?? null}
                        loading={attempts.loading}
                      />
                    </TD>
                    <TD>
                      <AudienceCell count={assignedCountOf(exam)} />
                    </TD>
                    <TD>
                      <Badge tone={STATUS_TONE[exam.status]}>{exam.status}</Badge>
                    </TD>
                    <TD align="right">
                      {/* The per-exam student URL. Offered here as well as on
                          the exam itself, because sending someone a link is
                          not a reason to have to open the exam. */}
                      <CopyButton
                        value={`${location.origin}/student/exam/${exam.id}/setup`}
                        label="Link"
                        copiedLabel="Copied"
                        title="Copy the link to send a student"
                      />
                    </TD>
                  </TR>
                ))
              )}
            </TBody>
          </Table>
        )}
      </Card>
    </div>
  );
}

/**
 * How many people have sat this exam, and how many are sitting it now. The
 * running count is the interesting half — it is the only thing on this screen
 * that is time-critical, so it gets the badge and the link to the monitor.
 */
function AttemptsCell({
  examId,
  counts,
  loading,
}: {
  examId: string;
  counts: { total: number; running: number } | null;
  loading: boolean;
}) {
  if (loading) return <span className="text-ink-faint">…</span>;
  if (counts === null || counts.total === 0)
    return <span className="text-ink-faint">None</span>;

  return (
    <div className="flex items-center gap-1.5">
      {/* Plain text, not a link: the attempts screen takes no exam filter yet,
          so a link here would promise a filtered list it cannot show. */}
      <span className="tabular text-ink-muted">{counts.total}</span>
      <LiveMonitorLink examId={examId} running={counts.running} />
    </div>
  );
}

/**
 * How many students an exam is assigned to, or `null` when this API build does
 * not report it.
 *
 * `assigned_count` is optional on `ExamSummary` and the admin's own `ExamRow`
 * (data/types.ts) does not declare it, so it is read defensively rather than
 * assumed: an absent field must render as "—" and not as "0 students", which
 * would say the exact opposite of the truth — zero assigned means the exam is
 * open to everyone.
 */
function assignedCountOf(
  exam: ExamRow & { assigned_count?: number },
): number | null {
  return typeof exam.assigned_count === "number" ? exam.assigned_count : null;
}

/**
 * Who the exam is for, in one cell. "Everyone" is a real, deliberate state —
 * it is what every exam created before rosters existed still is — so it gets
 * words rather than a zero.
 */
function AudienceCell({ count }: { count: number | null }) {
  if (count === null)
    return (
      <span
        className="text-ink-faint"
        title="This API build does not report the roster size."
      >
        —
      </span>
    );

  if (count === 0) return <Badge tone="accent">Everyone</Badge>;

  return (
    <span className="tabular text-ink-muted">
      {count} {count === 1 ? "student" : "students"}
    </span>
  );
}

/** `[1,2,3,5]` -> `1–3, 5`. Weeks are always contiguous-ish in practice. */
export function summariseWeeks(weeks: number[]): string {
  const sorted = [...weeks].sort((a, b) => a - b);
  const runs: string[] = [];
  let start = sorted[0]!;
  let prev = sorted[0]!;

  for (let i = 1; i <= sorted.length; i++) {
    const week = sorted[i];
    if (week !== undefined && week === prev + 1) {
      prev = week;
      continue;
    }
    runs.push(start === prev ? String(start) : `${start}–${prev}`);
    if (week === undefined) break;
    start = week;
    prev = week;
  }
  return runs.join(", ");
}
